import { cva, type VariantProps } from 'class-variance-authority'
import {
	Select as AriaSelect,
	type SelectProps as AriaSelectProps,
	Button,
	ListBox,
	ListBoxItem,
	Popover,
	SelectValue,
} from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Select} trigger button.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm (h-8), md (h-10, default), lg (h-12)
 * - Fluid width by default (`w-full`)
 * - Focus ring uses the brand primary color
 *
 * @example
 * ```tsx
 * const classes = selectVariants({ size: 'md' })
 * // Returns: "w-full rounded-ds-md border ... h-10 ..."
 * ```
 */
export const selectVariants = cva(
	'w-full rounded-ds-md border border-ds-border bg-ds-background px-3 text-ds-base text-ds-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-between',
	{
		variants: {
			size: {
				sm: 'h-8 text-ds-sm px-2',
				md: 'h-10 text-ds-base px-3',
				lg: 'h-12 text-ds-lg px-4',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Select} component.
 *
 * Extends React Aria's SelectProps with CVA size variants.
 *
 * @property size - Select size: 'sm' (h-8) | 'md' (h-10, default) | 'lg' (h-12)
 * @property className - Optional additional Tailwind classes
 * @property placeholder - Placeholder text when no value selected
 * @property isDisabled - Disabled state (React Aria pattern)
 * @property isRequired - Required field indicator
 * @property children - ListBoxItem elements
 *
 * @example
 * ```tsx
 * <Select size="md" placeholder="Select country">
 *   <ListBoxItem id="us">United States</ListBoxItem>
 *   <ListBoxItem id="uk">United Kingdom</ListBoxItem>
 * </Select>
 * ```
 */
export interface SelectProps<T extends object>
	extends Omit<AriaSelectProps<T>, 'className' | 'children'>,
		VariantProps<typeof selectVariants> {
	className?: string
	children: React.ReactNode
}

/**
 * Accessible select/dropdown primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation (Arrow keys, Enter, Esc), focus management,
 *   and ARIA attributes provided by React Aria's `<Select>` primitive.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small (h-8), medium (h-10, default), large (h-12).
 * - **Fluid width:** Uses `w-full` by default to fill container.
 * - **Focus indicator:** 2px ring in the brand primary color.
 * - **Keyboard:** Arrow keys navigate, Enter selects, Esc closes.
 *
 * @example Basic usage
 * ```tsx
 * import { Select, ListBoxItem } from '@atlas/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <Select placeholder="Select country" aria-label="Country">
 *       <ListBoxItem id="us">United States</ListBoxItem>
 *       <ListBoxItem id="uk">United Kingdom</ListBoxItem>
 *       <ListBoxItem id="es">Spain</ListBoxItem>
 *     </Select>
 *   )
 * }
 * ```
 *
 * @example With controlled value
 * ```tsx
 * const [country, setCountry] = useState<Key>('us')
 *
 * <Select
 *   selectedKey={country}
 *   onSelectionChange={setCountry}
 *   aria-label="Country"
 * >
 *   <ListBoxItem id="us">United States</ListBoxItem>
 *   <ListBoxItem id="uk">United Kingdom</ListBoxItem>
 * </Select>
 * ```
 *
 * @see {@link selectVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/select/select.stories.tsx for visual examples
 */
export function Select<T extends object>({ size, className, children, ...props }: SelectProps<T>) {
	return (
		<AriaSelect {...props} className="w-full">
			<Button className={cn(selectVariants({ size }), className)}>
				<SelectValue />
				<span aria-hidden="true" className="ml-2">
					▼
				</span>
			</Button>
			<Popover className="w-(--trigger-width) rounded-ds-md border border-ds-border bg-ds-background shadow-ds-lg">
				<ListBox className="max-h-60 overflow-auto p-1">{children}</ListBox>
			</Popover>
		</AriaSelect>
	)
}

/**
 * Re-export ListBoxItem for convenience.
 */
export { ListBoxItem }
